
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Shield, Users, BarChart3, Star } from "lucide-react";
import { useModalStack } from "@/hooks/useModalStack";

interface ComparisonTableProps {
  isOpen: boolean;
  onClose: () => void;
}

const ComparisonTable = ({ isOpen, onClose }: ComparisonTableProps) => {
  // Use our modal stack hook to manage nested dialogs
  const { handleOpenChange } = useModalStack(isOpen, onClose);
  
  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-6xl p-0 max-h-[90vh] overflow-y-auto">
        <div className="p-6 grid md:grid-cols-2 gap-8">
          {/* Free Strategies */}
          <div className="bg-white p-8 rounded-xl shadow-sm border border-[#D3DCE4]">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-[#E6F0F9] px-4 py-1 rounded-full">
                <span className="font-bold text-[#85B1DC]">FREE</span>
              </div>
              <h3 className="text-xl font-semibold text-[#1B2F47]">Free Strategies</h3>
            </div>
            
            <div className="space-y-6">
              <div className="flex gap-4">
                <Users className="w-6 h-6 text-[#012846] flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-[#1B2F47] mb-1">Open Access (Free)</h4>
                  <p className="text-[#4A607B]">Available to all users with no cost or subscription required.</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <Users className="w-6 h-6 text-[#012846] flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-[#1B2F47] mb-1">Community-Sourced</h4>
                  <p className="text-[#4A607B]">Strategies contributed by fellow traders in the METRADE community.</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <BarChart3 className="w-6 h-6 text-[#012846] flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-[#1B2F47] mb-1">Basic Performance Data</h4>
                  <p className="text-[#4A607B]">Essential metrics to gauge each strategy's performance.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Premium Strategies */}
          <div className="bg-gradient-to-br from-[#1B4066] to-[#012846] p-8 rounded-xl shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-[#F6C528] px-4 py-1 rounded-md">
                <span className="font-bold text-[#012846]">PREMIUM</span>
              </div>
              <h3 className="text-xl font-semibold text-white">Premium Strategies</h3>
            </div>
            
            <div className="space-y-6">
              <div className="flex gap-4">
                <Shield className="w-6 h-6 text-[#F6C528] flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-[#F6C528] mb-1">Expert-Vetted Strategies</h4>
                  <p className="text-white">Access proven strategies from professional traders vetted by METRADE's team.</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <BarChart3 className="w-6 h-6 text-[#F6C528] flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-[#F6C528] mb-1">Diversify Your Portfolio</h4>
                  <p className="text-white">Follow multiple strategies to spread risk and maximize returns.</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <Star className="w-6 h-6 text-[#F6C528] flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-[#F6C528] mb-1">Premium Support</h4>
                  <p className="text-white">Priority customer support whenever you need help or guidance.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ComparisonTable;
